
// CONVERSION / CTA DEFINITIONS

export type CTAType =
  | "cta_click"
  | "signup_submit"
  | "contact_submit"
  | "purchase"
  | "custom";

export interface CTADefinition {
  id: string;
  name: string;
  type: CTAType;
  /** Element IDs or event names that count as this CTA */
  eventMatchers: string[];
}

export const DEFAULT_CTAS: CTADefinition[] = [
  {
    id: "signup",
    name: "Signup Submit",
    type: "signup_submit",
    eventMatchers: ["signup", "signedUp"],
  },
  {
    id: "contact",
    name: "Contact Submit",
    type: "contact_submit",
    eventMatchers: ["contact", "contacted"],
  },
  {
    id: "cta_click",
    name: "CTA Click",
    type: "cta_click",
    eventMatchers: ["cta", "cta-button", "get-started"],
  },
];

// SEGMENT / FILTER DEFINITIONS

export type SegmentFilterType =
  | "referrer"
  | "source"
  | "landing_page"
  | "device"
  | "country"
  | "region"
  | "city"
  | "timezone"
  | "new_vs_returning"
  | "persona"
  | "screen_size"
  | "screen_width"
  | "screen_height"
  | "user_agent"
  // Conversion filters
  | "signed_up"
  | "contacted"
  | "email"
  // PageSession filters
  | "page_path"
  | "experiment"
  | "variant"
  // Numeric filters
  | "session_duration"
  | "pages_viewed"
  // Timestamp filters
  | "session_start"
  | "session_end";

/** String operators for text-based filters */
export type StringOperator =
  | "equals"
  | "not_equals"
  | "contains"
  | "not_contains"
  | "starts_with"
  | "ends_with"
  | "in"
  | "not_in";

/** Numeric operators for number-based filters */
export type NumericOperator =
  | "equals"
  | "not_equals"
  | "greater_than"
  | "greater_than_or_equals"
  | "less_than"
  | "less_than_or_equals"
  | "between";

/** Boolean operators */
export type BooleanOperator = "equals" | "not_equals";

/** Date operators for timestamp filters */
export type DateOperator =
  | "equals"
  | "not_equals"
  | "before"
  | "after"
  | "between"
  | "in_last_n_days"
  | "in_last_n_hours";

export type SegmentOperator =
  | StringOperator
  | NumericOperator
  | BooleanOperator
  | DateOperator;

/** Define what type of value each filter expects */
export type FilterValueType = "string" | "number" | "boolean" | "date";

/** Map filter types to their value types */
export const FILTER_VALUE_TYPES: Record<SegmentFilterType, FilterValueType> = {
  // String filters
  referrer: "string",
  source: "string",
  landing_page: "string",
  device: "string",
  country: "string",
  region: "string",
  city: "string",
  timezone: "string",
  new_vs_returning: "string",
  persona: "string",
  screen_size: "string",
  user_agent: "string",
  email: "string",
  page_path: "string",
  experiment: "string",
  variant: "string",
  // Numeric filters
  screen_width: "number",
  screen_height: "number",
  session_duration: "number",
  pages_viewed: "number",
  // Boolean filters
  signed_up: "boolean",
  contacted: "boolean",
  // Date filters
  session_start: "date",
  session_end: "date",
};

export interface SegmentFilter {
  id: string;
  type: SegmentFilterType;
  operator: SegmentOperator;
  value: string | string[];
}

export interface Segment {
  id: string;
  name: string;
  description?: string;
  filters: SegmentFilter[];
  /** ISO date string when the segment was created */
  createdAt: string;
  /** Whether this is a built-in segment */
  isBuiltIn?: boolean;
}

export const DEFAULT_SEGMENTS: Segment[] = [
  {
    id: "all",
    name: "All Traffic",
    filters: [],
    createdAt: new Date().toISOString(),
    isBuiltIn: true,
  },
];

// SUCCESS CRITERIA DEFINITIONS

/** Type of success criterion; the corresponding evaluator returns true when a session meets it. */
export type SuccessCriterionType =
  | "signed_up"
  | "contacted"
  | "visited_page"
  | "custom";

export interface SuccessCriterion {
  id: string;
  name: string;
  description?: string;
  type: SuccessCriterionType;
  /** For 'visited_page' type: the page path patterns to match */
  pagePatterns?: string[];
  /** ISO date string when created */
  createdAt: string;
  /** Whether this is a built-in criterion */
  isBuiltIn?: boolean;
}

export const DEFAULT_SUCCESS_CRITERIA: SuccessCriterion[] = [
  {
    id: "signed_up",
    name: "Signed Up",
    description: "User completed signup",
    type: "signed_up",
    createdAt: new Date().toISOString(),
    isBuiltIn: true,
  },
  {
    id: "visited_signin",
    name: "Visited signup page",
    description: "User visited the signup page",
    type: "visited_page",
    pagePatterns: ["/signup"],
    createdAt: new Date().toISOString(),
    isBuiltIn: true,
  },
  {
    id: "contacted",
    name: "Contacted",
    description: "User submitted a contact form",
    type: "contacted",
    createdAt: new Date().toISOString(),
    isBuiltIn: true,
  },
];

// GLOBAL FILTERS STATE

export interface DateRange {
  start: string; // ISO date string
  end: string; // ISO date string
}

export interface GlobalFilters {
  dateRange: DateRange;
  comparisonDateRange?: DateRange;
  compareEnabled: boolean;
  selectedCTA: CTADefinition;
  activeSegment: Segment;
  granularity: "hour" | "day" | "week" | "month";
}

// KPI / METRICS TYPES

export interface KPIMetric {
  id: string;
  label: string;
  value: number;
  previousValue?: number;
  change?: number; // percentage change
  changeDirection?: "up" | "down" | "neutral";
  format: "number" | "percentage" | "duration" | "currency";
  description?: string;
}

export interface TimeSeriesDataPoint {
  timestamp: string;
  value: number;
  comparisonValue?: number;
}

export interface TimeSeriesMetric {
  id: string;
  label: string;
  data: TimeSeriesDataPoint[];
  format: "number" | "percentage" | "duration";
}

// PAGE ANALYTICS TYPES

export interface PageMetrics {
  pagePath: string;
  pageTitle?: string;

  // Traffic metrics
  entrances: number; // sessions starting on this page
  sessions: number; // sessions that viewed this page
  pageviews: number; // total page views

  // Engagement metrics
  medianTimeOnPage: number; // seconds
  bounceRate: number; // 0-1
  exitRate: number; // 0-1

  // Conversion metrics
  samePageCTARate: number; // CTA clicks on this page / sessions
  assistRate: number; // users who saw page and later converted / total who saw page
  conversions: number; // CTA events on this page

  // UX signals (if available)
  deadClicks?: number;
  rageClicks?: number;
}

export interface PageDetailMetrics extends PageMetrics {
  // Element engagement
  topClickedElements: {
    elementId: string;
    clicks: number;
    clickRate: number;
  }[];
  elementTimeInView: { elementId: string; medianTime: number }[];

  // Navigation
  topPreviousPages: {
    pagePath: string;
    sessions: number;
    percentage: number;
  }[];
  topNextPages: { pagePath: string; sessions: number; percentage: number }[];

  // Mini funnel
  viewToInteraction: number; // rate of users who interacted with CTA area
  interactionToConversion: number; // rate of users who converted after interaction

  // Segment breakdown
  segmentComparison?: {
    segmentName: string;
    metrics: Partial<PageMetrics>;
  }[];
}

// FUNNEL TYPES

export interface FunnelStep {
  id: string;
  name: string;
  /** Matcher for pages or events that count as this step */
  matcher: string | string[];
}

export interface FunnelStepMetrics {
  step: FunnelStep;
  users: number;
  dropoffRate: number; // percentage who didn't proceed to next step
  conversionRate: number; // percentage of initial users who reached this step
  medianTimeToStep?: number; // seconds from previous step
}

export interface FunnelData {
  steps: FunnelStepMetrics[];
  totalStarted: number;
  totalCompleted: number;
  overallConversionRate: number;
}

// JOURNEY / PATH TYPES

export interface PathNode {
  pagePath: string;
  sessions: number;
}

export interface PathTransition {
  from: string;
  to: string;
  sessions: number;
  percentage: number;
}

export interface JourneyPath {
  path: string[]; // sequence of page paths
  sessions: number;
  conversionRate: number;
  medianDuration: number;
}

export interface JourneysData {
  topConversionPaths: JourneyPath[];
  topDropoffPaths: JourneyPath[];
  commonTransitions: PathTransition[];
  timeToConvertDistribution: { bucket: string; count: number }[];
}

// ACQUISITION TYPES

export interface AcquisitionSource {
  source: string; // referrer, campaign, etc.
  sessions: number;
  engagedSessionRate: number; // 0-1
  ctaConversionRate: number; // 0-1
  downstreamConversionRate?: number; // 0-1
  topLandingPages?: { pagePath: string; sessions: number }[];
}

export interface AcquisitionData {
  bySource: AcquisitionSource[];
  byReferrer: AcquisitionSource[];
}

// COHORT TYPES

export type CohortType = "time_based" | "acquisition_based" | "behavioral";

export interface CohortDefinition {
  id: string;
  name: string;
  type: CohortType;
  description?: string;
  /** Filter criteria specific to cohort type */
  criteria: Record<string, unknown>;
}

export interface CohortComparison {
  cohortA: CohortDefinition;
  cohortB: CohortDefinition;
  metricsComparison: {
    metricName: string;
    cohortAValue: number;
    cohortBValue: number;
    difference: number;
    differencePercentage: number;
  }[];
}

// UX SIGNALS TYPES

export interface UXSignals {
  pagePath: string;
  deadClickRate: number; // 0-1
  rageClickRate: number; // 0-1
  formStartRate?: number; // 0-1
  formSubmitRate?: number; // 0-1
  highTimeNoConversionElements: {
    elementId: string;
    medianTimeInView: number;
    conversionRate: number;
  }[];
}

// OVERVIEW / DASHBOARD SUMMARY TYPES

export interface OverviewData {
  kpis: KPIMetric[];
  sessionsTrend: TimeSeriesMetric;
  conversionTrend: TimeSeriesMetric;
  whatChanged: {
    category: "source" | "landing_page" | "device";
    item: string;
    metricName: string;
    previousValue: number;
    currentValue: number;
    changePercentage: number;
  }[];
}

// DEFINITIONS & ENGAGEMENT CONFIG

export interface EngagementDefinition {
  /** Minimum time on page (seconds) to count as engaged */
  minTimeOnPage: number;
  /** Minimum pages viewed to count as engaged */
  minPagesViewed: number;
  /** Elements that indicate engagement when clicked */
  engagementElements?: string[];
}

export const DEFAULT_ENGAGEMENT: EngagementDefinition = {
  minTimeOnPage: 10,
  minPagesViewed: 2,
};

// DASHBOARD STATE TYPE

export type DashboardSection =
  | "overview"
  | "sessions"
  | "pages"
  | "journeys"
  | "acquisition";

export interface DashboardState {
  activeSection: DashboardSection;
  savedSegments: Segment[];
  engagementDefinition: EngagementDefinition;
}
